import React, { useEffect, useState } from "react";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { Box, Button, Stack } from "@mui/material";

import { ButtonVariant } from "Constant/button";
import { routes } from "Constant/routes";
import { useNavigate, useParams } from "react-router-dom";

import { LeadReportStatus } from "helpers/leads/generalReports";
import {
    LEAD_HISTORY_MAX_PAGE_SIZE,
    LeadHistoryRow,
} from "helpers/leads/reports";
import { renderResponseMessage } from "helpers/leads/responseMessageRenderer";

import PageContainer from "components/atoms/PageContainer";
import StandartButton from "components/atoms/StandartButton";
import GenericTable from "components/organisms/GenericTable";
import { GenericTableColumn } from "components/organisms/GenericTable/GenericTable.types";

import * as Styled from "../../LeadGeneralReportsPage/LeadGeneralReportsPage.styled";
import { STATUS_COLORS } from "../../LeadGeneralReportsPage/LeadGeneralReportsPage.types";
import HistoryFilterBar from "./components/HistoryFilterBar";
import LeadRetryButton from "./components/LeadRetryButton";
import {
    DEFAULT_LEAD_HISTORY_FILTERS,
    formatHistoryDate,
    getLeadHistoryFilterError,
    LeadHistoryFilters,
    toLeadHistoryQuery,
} from "./LeadHistoryPage.helpers";
import type { LeadHistoryPageProps } from "./LeadHistoryPage.types";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 10;

const LeadHistoryStatusCell = ({ row }: { row: LeadHistoryRow }) => {
    const [expanded, setExpanded] = useState(false);
    const message = renderResponseMessage(row.last_response);
    const text = typeof message === "string" ? message : "";
    const isLong = text.length > 40;
    const meta = STATUS_COLORS[row.status as LeadReportStatus];

    return (
        <Stack gap={0.5} sx={{ whiteSpace: "normal" }}>
            {meta ? (
                <Styled.StatusChip $bg={meta.bg} $color={meta.text}>
                    {meta.label}
                </Styled.StatusChip>
            ) : (
                <Box sx={{ fontWeight: 600 }}>{row.status}</Box>
            )}
            {text && (
                <Box>
                    <Box
                        sx={{
                            fontSize: 12,
                            lineHeight: "18px",
                            color: "text.secondary",
                            whiteSpace: expanded ? "pre-wrap" : "nowrap",
                            wordBreak: expanded ? "break-all" : "normal",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxHeight: expanded ? "none" : 18,
                        }}
                    >
                        {text}
                    </Box>
                    {isLong && (
                        <Box
                            component="button"
                            type="button"
                            onClick={() => setExpanded((open) => !open)}
                            sx={{
                                border: 0,
                                background: "none",
                                padding: 0,
                                color: "primary.main",
                                cursor: "pointer",
                                fontSize: 12,
                            }}
                        >
                            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            {expanded ? "Hide details" : "Show details"}
                        </Box>
                    )}
                </Box>
            )}
        </Stack>
    );
};

const LeadHistoryActions = ({
    row,
    loading,
    onRetry,
}: {
    row: LeadHistoryRow;
    loading: boolean;
    onRetry: (statId: number) => void;
}) => {
    if (!row.scheduled && !row.retry_available) return null;

    return (
        <LeadRetryButton
            scheduled={row.scheduled}
            loading={loading}
            onClick={() => onRetry(row.stat_id)}
        />
    );
};

const LeadHistoryPage = ({
    leads,
    pagination,
    isLeadHistoryLoading,
    leadHistoryError,
    isRetryLoading,
    retryError,
    retryingStatId,
    dispatchGetLeadHistory,
    dispatchRetryLeadHistory,
    dispatchClearLeadHistory,
}: LeadHistoryPageProps) => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [filters, setFilters] = useState<LeadHistoryFilters>(
        DEFAULT_LEAD_HISTORY_FILTERS,
    );
    const [pendingFilters, setPendingFilters] = useState<LeadHistoryFilters>(
        DEFAULT_LEAD_HISTORY_FILTERS,
    );

    const query = toLeadHistoryQuery(filters);

    useEffect(() => {
        return () => {
            dispatchClearLeadHistory();
        };
    }, [dispatchClearLeadHistory]);

    useEffect(() => {
        if (!id) {
            navigate(routes.connection);
            return;
        }
        dispatchGetLeadHistory({
            connectionId: id,
            page,
            page_size: pageSize,
            status: query.status,
            start_date: query.start_date,
            end_date: query.end_date,
        });
    }, [
        id,
        navigate,
        page,
        pageSize,
        query.status,
        query.start_date,
        query.end_date,
        dispatchGetLeadHistory,
    ]);

    const handleApply = () => {
        if (getLeadHistoryFilterError(pendingFilters)) return;
        setPage(1);
        setFilters({
            ...pendingFilters,
            statuses: [...pendingFilters.statuses],
        });
    };

    const handleClearFilters = () => {
        setPage(1);
        setFilters({
            ...DEFAULT_LEAD_HISTORY_FILTERS,
            statuses: [...DEFAULT_LEAD_HISTORY_FILTERS.statuses],
        });
        setPendingFilters({
            ...DEFAULT_LEAD_HISTORY_FILTERS,
            statuses: [...DEFAULT_LEAD_HISTORY_FILTERS.statuses],
        });
    };

    const handleRetry = (statId: number) => {
        if (!id) return;
        dispatchRetryLeadHistory({
            connectionId: id,
            stat_ids: [statId],
            source: "history",
            listParams: {
                page,
                page_size: pageSize,
                status: query.status,
                start_date: query.start_date,
                end_date: query.end_date,
            },
        });
    };

    const columns: GenericTableColumn<LeadHistoryRow>[] = [
        {
            key: "date",
            header: "Date",
            label: "Date",
            minWidth: 180,
            render: (row) => formatHistoryDate(row.date),
        },
        {
            key: "lead_id",
            header: "LEAD ID",
            label: "LEAD ID",
            minWidth: 160,
            render: (row) => String(row.lead_id ?? "—"),
        },
        {
            key: "status",
            header: "Status",
            label: "Status",
            minWidth: 220,
            render: (row) => <LeadHistoryStatusCell row={row} />,
        },
        {
            key: "actions",
            header: "Actions",
            label: "Actions",
            minWidth: 160,
            fixedWidth: 172,
            cardActions: true,
            render: (row) => (
                <LeadHistoryActions
                    row={row}
                    loading={isRetryLoading && retryingStatId === row.stat_id}
                    onRetry={handleRetry}
                />
            ),
        },
    ];

    return (
        <PageContainer
            title="Lead History"
            subtitle="Delivery records for this connection"
            loading={isLeadHistoryLoading && leads.length === 0}
            containerClassName="tracker-detail-area"
        >
            <Stack gap={3}>
                <HistoryFilterBar
                    filters={pendingFilters}
                    onChange={setPendingFilters}
                    onApply={handleApply}
                    isLoading={isLeadHistoryLoading}
                />
                {(leadHistoryError || retryError) && (
                    <Styled.ErrorBanner>
                        <Stack direction="row" gap={1} alignItems="center">
                            <ErrorOutlineIcon sx={{ fontSize: 18 }} />
                            {retryError || leadHistoryError}
                        </Stack>
                        {leadHistoryError && (
                            <Button
                                color="primary"
                                onClick={() =>
                                    id &&
                                    dispatchGetLeadHistory({
                                        connectionId: id,
                                        page,
                                        page_size: pageSize,
                                        status: query.status,
                                        start_date: query.start_date,
                                        end_date: query.end_date,
                                    })
                                }
                                size="small"
                                startIcon={
                                    <RefreshIcon sx={{ fontSize: 16 }} />
                                }
                                variant="contained"
                            >
                                Try Again
                            </Button>
                        )}
                    </Styled.ErrorBanner>
                )}
                <Styled.SectionCard elevation={0}>
                    <GenericTable
                        columns={columns}
                        rows={leads}
                        getRowKey={(row) => row.stat_id}
                        loading={isLeadHistoryLoading}
                        ellipsis={false}
                        emptyState={
                            <Styled.EmptyStateWrap>
                                <Box
                                    sx={{
                                        width: 72,
                                        height: 72,
                                        borderRadius: "50%",
                                        background: "#F1F5F9",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "#94A3B8",
                                    }}
                                >
                                    <SearchOffIcon sx={{ fontSize: 36 }} />
                                </Box>
                                <Box
                                    sx={{
                                        fontSize: 18,
                                        fontWeight: 700,
                                        color: "#0F172A",
                                    }}
                                >
                                    No data found
                                </Box>
                                <Box
                                    sx={{
                                        fontSize: 13,
                                        color: "#64748B",
                                        maxWidth: 420,
                                    }}
                                >
                                    There are no lead records for the selected
                                    filters. Try adjusting the date range or
                                    filters.
                                </Box>
                                <StandartButton
                                    variant={ButtonVariant.BLACK_OUTLINED}
                                    onClick={handleClearFilters}
                                >
                                    Clear Filters
                                </StandartButton>
                            </Styled.EmptyStateWrap>
                        }
                        currentPage={pagination.current_page || page}
                        totalPages={pagination.last_page || 1}
                        totalItems={pagination.total || 0}
                        onPageChange={setPage}
                        pageSize={pageSize}
                        onPageSizeChange={(next) => {
                            setPageSize(
                                Math.min(next, LEAD_HISTORY_MAX_PAGE_SIZE),
                            );
                            setPage(1);
                        }}
                        pageSizeOptions={PAGE_SIZE_OPTIONS}
                    />
                </Styled.SectionCard>
            </Stack>
        </PageContainer>
    );
};

export default LeadHistoryPage;
